/**
 * Одна активная вкладка пульта на браузер.
 *
 * У SIP-аккаунта диспетчера max_contacts=1 (спека §7.8): вторая вкладка вытеснит
 * регистрацию первой, и диспетчер получит тишину при зелёном индикаторе. Поэтому
 * лидерство берётся эксклюзивным Web Lock: браузер снимает лок сам при крахе вкладки,
 * ни таймаутов, ни heartbeat не нужно. BroadcastChannel — только чтобы остальные
 * вкладки сразу перерисовались в режим чтения, а не ждали освобождения лока.
 */

const LOCK_NAME = 'softphone';
const CHANNEL_NAME = 'cc.softphone.leader';
/** Просьба отдать линию: лидер отпускает лок, очередь забирает следующий */
const TAKEOVER = 'takeover';

export interface TabLeaderOptions {
  onChange: (isLeader: boolean) => void;
  /** Подменяется в тестах */
  locks?: LockManager;
  createChannel?: () => BroadcastChannel | null;
}

export class TabLeader {
  readonly #options: TabLeaderOptions;
  readonly #locks: LockManager | null;
  #channel: BroadcastChannel | null = null;
  #release: (() => void) | null = null;
  #isLeader = false;
  #running = false;

  constructor(options: TabLeaderOptions) {
    this.#options = options;
    this.#locks = options.locks ?? (typeof navigator !== 'undefined' ? navigator.locks : null);
  }

  isLeader(): boolean {
    return this.#isLeader;
  }

  start(): void {
    if (this.#running) return;
    this.#running = true;

    this.#channel =
      this.#options.createChannel?.() ??
      (typeof BroadcastChannel !== 'undefined' ? new BroadcastChannel(CHANNEL_NAME) : null);

    // Лидер слушает просьбу отдать линию: диспетчер нажал «Работать здесь»
    // во второй вкладке. Без этого вторая вкладка молчит до закрытия первой,
    // а закрыть её человек не может — он её просто не находит (10.09.2026)
    if (this.#channel !== null) {
      this.#channel.onmessage = (event: MessageEvent<{ t?: string }>) => {
        if (event.data.t !== TAKEOVER || !this.#isLeader) return;
        this.#yield();
      };
    }

    if (!this.#locks) {
      // Web Locks нет (старый браузер) — работаем как единственная вкладка.
      // Хуже, чем блокировка, но лучше, чем неработающий пульт.
      this.#setLeader(true);
      return;
    }

    this.#acquire();
  }

  /**
   * Забрать линию в эту вкладку.
   *
   * Web Lock отобрать нельзя, поэтому просим лидера отпустить: наша заявка на
   * лок уже стоит в очереди с самого start(), и как только он отпустит — линия
   * наша. Лидер тут же встаёт в очередь сам, так что закрытие этой вкладки
   * вернёт ему линию.
   */
  requestTakeover(): void {
    if (this.#isLeader) return;
    this.#channel?.postMessage({ t: TAKEOVER });
  }

  /** Отпустить лок и снова встать в очередь за ним */
  #yield(): void {
    this.#release?.();
    this.#release = null;
    if (this.#running && this.#locks) this.#acquire();
  }

  #acquire(): void {
    if (!this.#locks) return;
    void this.#locks.request(LOCK_NAME, { mode: 'exclusive' }, async () => {
      if (!this.#running) return;
      this.#setLeader(true);
      this.#channel?.postMessage({ t: 'leader_taken' });
      // Лок держится, пока не разрешится этот промис: отпускаем в stop() и #yield()
      await new Promise<void>((resolve) => {
        this.#release = resolve;
      });
      this.#setLeader(false);
    });
  }

  stop(): void {
    this.#running = false;
    this.#release?.();
    this.#release = null;
    this.#channel?.close();
    this.#channel = null;
    this.#setLeader(false);
  }

  #setLeader(value: boolean): void {
    if (this.#isLeader === value) return;
    this.#isLeader = value;
    this.#options.onChange(value);
  }
}
